import { DivergenceStatusEnum } from "@/enum/divergence-status";
import { PayrollVoucherStatusEnum } from "@/enum/payroll-voucher-status";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  IS_LOSS_FILTER_VALUE,
  LINK_BLUE_HEX,
  MANAGE_LOSS_FILTERS_STORAGE_KEY,
  MANAGE_LOSS_PATH,
  PAYROLL_DISCOUNT_FILTER_VALUE,
} from "../constants";
import { useGetDashboardPendingActionsQuery } from "@/services/conciliation-dashboard";
import { DASHBOARD_LABELS } from "../messages";
import { PendingActionItem } from "../types";

const AWAITING_MANAGER_STATUSES: readonly string[] = [
  DivergenceStatusEnum.PENDING,
];

const buildPendingActionItems = (
  counts: Record<string, number>
): readonly PendingActionItem[] => [
  {
    key: "pending-losses",
    count: counts.pendingLosses,
    label: DASHBOARD_LABELS.PENDING_LOSSES,
    containerClassName: "bg-[#F6D040]/10 border-[#F6D040]/30 text-[#613F4F]",
    filters: {
      status: [...AWAITING_MANAGER_STATUSES],
      type: [IS_LOSS_FILTER_VALUE],
    },
  },
  {
    key: "waiting-hr",
    count: counts.waitingHr,
    label: DASHBOARD_LABELS.WAITING_HR,
    containerClassName: "bg-blue-50 border-blue-200 text-blue-900",
    filters: {
      payroll_voucher_status: [PayrollVoucherStatusEnum.PENDING],
      type: [PAYROLL_DISCOUNT_FILTER_VALUE],
    },
  },
  {
    key: "rejected-hr",
    count: counts.rejectedByHr,
    label: DASHBOARD_LABELS.REJECTED_BY_HR,
    containerClassName: "bg-red-50 border-red-200 text-red-900",
    filters: {
      payroll_voucher_status: [PayrollVoucherStatusEnum.REJECTED],
      type: [PAYROLL_DISCOUNT_FILTER_VALUE],
    },
  },
];

export function PendingActions(): JSX.Element {
  const { data } = useGetDashboardPendingActionsQuery();

  const PENDING_ACTION_ITEMS: readonly PendingActionItem[] =
    buildPendingActionItems({
      pendingLosses: data?.pending_losses ?? 0,
      waitingHr: data?.waiting_hr ?? 0,
      rejectedByHr: data?.rejected_by_hr ?? 0,
    });

  const navigate = useNavigate();

  const goToManageLoss = (filters: Record<string, string[]>): void => {
    localStorage.setItem(
      MANAGE_LOSS_FILTERS_STORAGE_KEY,
      JSON.stringify(filters)
    );
    navigate(MANAGE_LOSS_PATH);
  };

  return (
    <div className="w-full space-y-2">
      <p className="text-sm font-semibold text-[#613F4F]">
        {DASHBOARD_LABELS.PENDING_ACTIONS_TITLE}
      </p>
      {PENDING_ACTION_ITEMS.map((item: PendingActionItem) => (
        <div
          key={item.key}
          className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 ${item.containerClassName}`}
        >
          <p className="text-sm">
            <span className="font-bold">{item.count}</span> {item.label}
          </p>
          <button
            type="button"
            onClick={() => goToManageLoss(item.filters)}
            className="flex shrink-0 items-center gap-1 text-sm font-medium hover:underline"
            style={{ color: LINK_BLUE_HEX }}
          >
            {DASHBOARD_LABELS.SEE_ITEMS}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
